import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import apiClient from 'src/helpers/apiClient';
import type { RootState } from 'src/redux/index';
import type { FeedbackBody } from 'src/openapi';
import config from '../../../../config';

const getCreditsConsumptionHistory = createAsyncThunk(
  'user/credits-consumption-history',
  async (
    {
      page,
      page_size,
      transaction_type,
    }: { page: number; page_size: number; transaction_type: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getCreditsApi()
      ).creditsApiGetCreditConsumptionHistory({
        page,
        pageSize: page_size,
        transactionType: transaction_type,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

let userSkillAbortController: AbortController | null = null;
export const getUserSkills = createAsyncThunk(
  'get/user-skills',
  async (
    {
      searchQuery,
      pageNo = 1,
      pageSize = 10,
      bypassAbort = false,
    }: {
      searchQuery: string;
      pageNo?: number;
      pageSize?: number;
      bypassAbort?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      if (!bypassAbort && userSkillAbortController) {
        userSkillAbortController.abort();
      }
      userSkillAbortController = new AbortController();
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillList(
        { page: pageNo, pageSize, searchQuery },
        {
          signal: userSkillAbortController.signal,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

let userSkillBuilderDataAbortController: AbortController | null = null;
export const getUserSkillBuilderData = createAsyncThunk(
  'get/user-skill-builder-data',
  async (
    {
      searchQuery,
      coreOnly,
      bypassAbort = false,
    }: {
      searchQuery?: string;
      coreOnly?: boolean;
      bypassAbort?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      if (!bypassAbort && userSkillBuilderDataAbortController) {
        userSkillBuilderDataAbortController.abort();
      }
      const controller = new AbortController();
      if (!bypassAbort) userSkillBuilderDataAbortController = controller;
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillListV2(
        { coreOnly, searchQuery, searchType: undefined },
        { signal: controller.signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getSkillsBasedOnJobSkills = createAsyncThunk(
  'get/skills-based-on-job-skills',
  async (
    {
      jobSkills,
      pageNo = 1,
      pageSize = 10,
    }: {
      jobSkills: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getSkillsApi()
      ).skillApiGetSkillList({
        page: pageNo,
        pageSize,
        searchQuery: jobSkills,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getExams = createAsyncThunk(
  'get/exams',
  async ({ query }: { query?: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getExamApi()
      ).examApiExamAutoComplete({ query });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const changeDomain = createAsyncThunk(
  'change/domain',
  async (domainId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const response = await axios.put(
        `${config.API_URL}/user/select_domain`,
        { domain_id: domainId },
        {
          headers: {
            Authorization: `Bearer ${state.auth.token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const postUserFeedbackReport = createAsyncThunk(
  'user/feedback-report',
  async ({ data }: { data: FeedbackBody }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiCreateFeedbackReport({ feedbackBody: data });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadUserFeedbackMedia = createAsyncThunk(
  'user/feedback-media',
  async ({ file }: { file: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getGeneralApi()
      ).generalApiUploadFeedbackMedia({ mediaFile: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const unlockSolution = createAsyncThunk(
  'get/unlock-solution',
  async ({ questionId }: { questionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getQuestionApi()
      ).questionApiGetSolution({ questionId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const triggerMailUnsubscribe = createAsyncThunk(
  'mail/unsubscribe',
  async (
    {
      unsubscribeMailBody,
    }: {
      unsubscribeMailBody: {
        reason_to_unsubscribe: string;
        preferred_platform: string;
        email_settings_id: string;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getMailAPi()
      ).mailApiUnsubscribeEmail({
        unsubscribeEmailBody: unsubscribeMailBody,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export default getCreditsConsumptionHistory;
